import numeral from 'numeral';

//Create a default formatter
numeral.defaultFormat('0,0.00');

export const formatDefaultFractionTwo = (input: unknown) => {
  return numeral(input).format();
};

export const formatFractionSix = (input: unknown) => {
  return numeral(input).format('0,0.000000');
};

export const formatMaxSix = (input: unknown) => {
  return numeral(input).format('0,0.00[0000]');
};

export const formatNumberWithLetter = (input: unknown) => {
  return numeral(input)
    .format('0.[00]a')
    .toLowerCase()
    .replace(/[a-z]\b/g, (c) => c.toUpperCase());
};

export const formatAmountWithLetterOrDash = (value: number | null | undefined, prefix = '$') =>
  value == null ? '—' : `${prefix}${formatNumberWithLetter(value)}`;

export const formatFillPercentage = (value: number) =>
  value < 1 && value > 0 ? Number(value.toFixed(2)) : Math.round(value * 100) / 100;

/** "7%" — a percentage already expressed in percent units; "—" when absent */
export const formatPercent = (value?: number | null) =>
  typeof value === 'number' ? `${formatDefaultFractionTwo(value)}%` : '—';

/**
 * Rounds a 0–1 fraction (e.g. `0.82098` for an offer 82% filled) to a clamped
 * whole percent. Null/undefined pass through as null rather than 0 — a fill
 * figure the board never reported is "unknown", not "0% filled".
 */
export const toPercentFromFraction = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.min(100, Math.max(0, Math.round(value * 100)))
    : null;

export const formatCompactCurrencyAmount = (amount: number, symbol = '$') => {
  const sign = amount < 0 ? '-' : '';
  const absoluteValue = Math.abs(amount);

  if (absoluteValue >= 1_000_000_000) {
    return `${sign}${symbol}${formatCompactUnit(absoluteValue / 1_000_000_000)}b`;
  }
  if (absoluteValue >= 1_000_000) {
    return `${sign}${symbol}${formatCompactUnit(absoluteValue / 1_000_000)}m`;
  }
  if (absoluteValue >= 1_000) {
    return `${sign}${symbol}${formatCompactUnit(absoluteValue / 1_000)}k`;
  }

  return `${sign}${symbol}${formatCompactUnit(absoluteValue)}`;
};

export const formatWithoutFraction = (input: unknown) => {
  return numeral(input).format('0,0');
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CHF: 'CHF ',
};

/**
 * "$" / "€" / "JPY " — the code with a trailing space when there is no symbol
 * for it. A fund may carry no currency at all, and a bare amount reads better
 * there than the code's own absence rendered as text.
 */
export const getCurrencySymbol = (currency: string | null | undefined) =>
  currency ? (CURRENCY_SYMBOLS[currency] ?? `${currency} `) : '';

/** "$30,000" / "€22,000" — whole-number money with a currency symbol. */
export const formatMoney = (amount: number, currency: string | null | undefined) =>
  `${getCurrencySymbol(currency)}${formatWithoutFraction(amount)}`;

export const formatIntOrMaxTwo = (input: unknown) => {
  return numeral(input).format('0,0.[00]');
};

export const getMillionsScaleUnit = (valueInMillions: number): 'T' | 'B' | 'M' => {
  const abs = Math.abs(valueInMillions);
  if (abs >= 1_000_000) return 'T';
  if (abs >= 1_000) return 'B';
  return 'M';
};

export const formatMillions = (value: number, inMillions: boolean = true): string => {
  if (!Number.isFinite(value)) return '';

  const valueInMillions = inMillions ? value : value / 1_000_000;
  const unit = getMillionsScaleUnit(valueInMillions);

  if (unit === 'T') return `${formatIntOrMaxTwo(valueInMillions / 1_000_000)}T`;
  if (unit === 'B') return `${formatIntOrMaxTwo(valueInMillions / 1_000)}B`;
  return `${formatIntOrMaxTwo(valueInMillions)}M`;
};

const formatCompactUnit = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');

export const splitAmountParts = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return { integer: '—', fraction: '' };

  const [integer, fraction = '00'] = Math.abs(value).toFixed(2).split('.');
  const grouped = Number(integer).toLocaleString('en-US');

  return { integer: value < 0 ? `-${grouped}` : grouped, fraction };
};
